import type { PartnerCredentialSummary } from "@tdelivr/api-client/generated/partner-api/partner-credential.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the partner API board does to the summaries API-02 returns, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `partner`, against API-02
 * `GET /v1/platform/partner-credentials` — published by
 * [ADR 0052](../../../docs/adr/0052-a-platform-scoped-partner-credential-listing-is-a-separate-operation-and-a-separate-scope.md)
 * because the only public listing this service had was "scoped to the authenticated tenant", and a
 * board of every partner on the platform may not be drawn from one (ADR 0048).
 *
 * ```
 * partner:['Partner API','API-02 · API-03','Partner credentials and webhook delivery.',
 *   [['Partners','11','done','active'],['Webhooks failing','1','failed','endpoint 500'],
 *    ['Keys expiring','2','caution','30 days'],['Deliveries today','18,402','done','']],
 *   ['Partner','Credential','Webhook'],
 *   [['Kilimani Fresh POS','key ···9F2A','delivered 99.8%','done','healthy'],
 *    ['Harbour Rx ERP','key ···11B0 · expires 14 days','delivered 100%','caution','key expiring'],
 *    ['Northline WMS','key ···77C1','failing · 500','failed','retrying with backoff']],
 *   'info','Secrets are shown once at creation, then only as the last four characters.']
 * ```
 *
 * ## `key ···9F2A` has a source, and it is not the one three sweeps looked for
 *
 * `tests/contract/console-screen-support.test.ts` records this element as an `absent` need over
 * `credential.maskedSecret`, `credential.lastFour` and `credential.secretSuffix`, and nothing
 * publishes any of the three. **API-02 publishes `credentials.clientIdSuffix`**, which is this cell,
 * under a fourth name nobody swept for — found by ADR 0048 and recorded as R-59 and Q-181. So the
 * sweep is right about its three names and wrong about the element, and this board draws it.
 *
 * **It is the client identifier's last four characters and not the secret's**, which is the honest
 * difference and the reason ADR 0044 published it. The design's own closing note says "secrets are
 * shown once at creation, then only as the last four characters", and that second clause is not true
 * of anything in this service: a secret is kept as an argon2id digest and no four characters can be
 * recovered from one. The client identifier is a public value whose last four characters the design's
 * grammar can carry honestly. Q-150 is where that lives.
 *
 * The suffix is upper-cased for display. The design writes `9F2A`; `clientIdSuffix` is the stored
 * UUID's own lowercase hex, `9f2a`. A hex identifier's case carries no information and the design
 * has decided which it draws, so the transformation is a rendering rather than a change to data —
 * said here because it is the one value on this board that is not shown exactly as published.
 *
 * ## `Webhook` has no source at all, and that is this page's remaining hole
 *
 * The design's third column is `delivered 99.8%`, `delivered 100%`, `failing · 500`. That is API-03's
 * delivery outcome, and **API-03 publishes nothing a browser can call**: three operations, all under
 * `/internal/v1`, plus `/v1/service-info`. Its `Webhooks failing · 1 · endpoint 500` tile and its
 * `retrying with backoff` badge are the same absence.
 *
 * So the column is drawn with the design's own `—`, carrying the reason on its `title`, rather than
 * removed or filled. A removed column silently changes the grid's shape; an invented value is what
 * `CLAUDE.md` section 2 forbids. **Q-195** records it, and it is API-03's own decision to publish a
 * delivery summary rather than one this change could take — ADR 0052 §6 says so and why.
 *
 * ## `Partner` is the credential's display name, which is what a partner is called here
 *
 * The design's first column is a partner's name. API-02 holds no partner aggregate — `partnerId`
 * arrives on the issue command from whoever registered the partner, and nothing on this platform
 * publishes a list of a tenant's partners. What it does hold is `displayName`, the name an issuer
 * gave the credential, which in the design's own sample is exactly a partner's name
 * (`Kilimani Fresh POS`). So the column is that, and a partner with two credentials draws two rows.
 * **Q-196** records the difference between a credential and a partner, which the `Partners · 11`
 * tile is also about.
 */

/** The design's own credential grammar: `key ···9F2A`. */
export function credentialText(credential: PartnerCredentialSummary): string {
  return `key ···${credential.clientIdSuffix.toUpperCase()}`;
}

/** Days from now until a credential expires, negative once it has. */
export function daysUntil(instant: string, now: Date): number | undefined {
  const at = Date.parse(instant);
  if (!Number.isFinite(at)) return undefined;
  return Math.floor((at - now.getTime()) / 86_400_000);
}

/**
 * How near expiry the design calls "expiring", which is its own `Keys expiring · 2 · 30 days` tile.
 *
 * Thirty days, from that tile's own subtitle, rather than the fourteen its second row happens to
 * show: the tile states the rule and the row states one key's remaining life.
 */
export const EXPIRING_WITHIN_DAYS = 30;

/**
 * The state class and the word, from `status` and the expiry together.
 *
 * The design's three badges settle both the mapping and its precedence:
 *
 * | the design's row                           | class     | word                     |
 * | ------------------------------------------ | --------- | ------------------------ |
 * | `Kilimani Fresh POS · key ···9F2A`         | `done`    | `healthy`                |
 * | `Harbour Rx ERP · … · expires 14 days`     | `caution` | `key expiring`           |
 * | `Northline WMS · … · failing · 500`        | `failed`  | `retrying with backoff`  |
 *
 * The third is API-03's and is unreachable here, so this board can draw the first two and the two
 * states API-02 publishes that the design has no row for: a credential awaiting its second approval,
 * which is `human` because that is a row needing a person, and a revoked or expired one, which is
 * `closed`. Both are states the contract certainly has — `status` is
 * `pending_approval | active | revoked` — and neither is invented: the words are the contract's own.
 *
 * Expiry outranks nothing: a revoked key is closed whatever its `expiresAt` says, and an active key
 * past its own expiry is not usable — `isCredentialUsable` compares the instant — so it is `closed`
 * rather than `caution`, with the reason on the badge.
 */
export function stateOf(
  credential: PartnerCredentialSummary,
  now: Date,
): { classKey: StateClassKey; label: string; title?: string } {
  if (credential.status === "revoked") {
    return {
      classKey: "closed",
      label: "revoked",
      ...(credential.revokedAt === null
        ? {}
        : { title: `Revoked ${asDay(credential.revokedAt)}. Introspection refuses it.` }),
    };
  }
  if (credential.status === "pending_approval") {
    return {
      classKey: "human",
      label: "awaiting approval",
      title:
        "Issued and not yet activated. API-02 requires a transition before introspection accepts it, and the design has no row for this state.",
    };
  }

  const days = daysUntil(credential.expiresAt, now);
  if (days !== undefined && days < 0) {
    return {
      classKey: "closed",
      label: "expired",
      title: `Expired ${asDay(credential.expiresAt)}. The stored status is still active; API-02 compares the instant when it introspects, so this key no longer works.`,
    };
  }
  if (days !== undefined && days <= EXPIRING_WITHIN_DAYS) {
    return { classKey: "caution", label: "key expiring" };
  }
  return { classKey: "done", label: "healthy" };
}

/** The design's `expires 14 days`, appended to the credential cell as it appends it. */
export function expiryText(credential: PartnerCredentialSummary, now: Date): string | undefined {
  if (credential.status === "revoked") return undefined;
  const days = daysUntil(credential.expiresAt, now);
  if (days === undefined || days > EXPIRING_WITHIN_DAYS) return undefined;
  if (days < 0) return `expired ${asDay(credential.expiresAt)}`;
  return `expires ${String(days)} day${days === 1 ? "" : "s"}`;
}

/** One row of the partner API board. */
export function credentialRow(credential: PartnerCredentialSummary, now: Date): BoardRow {
  const state = stateOf(credential, now);
  const expiry = expiryText(credential, now);
  return {
    /*
     * The tenant, the partner and the credential, which is API-02's primary key and this listing's
     * cursor. Prefixed with the tenant because the listing spans tenants: a React key that collided
     * would make one tenant's row replace another's on a board whose purpose is seeing all of them.
     */
    id: `${credential.tenantId}:${credential.partnerId}:${credential.credentialId}`,
    cells: [
      { text: credential.displayName },
      {
        // The design's own grammar: the key, and the expiry beside it when there is one.
        text:
          expiry === undefined
            ? credentialText(credential)
            : `${credentialText(credential)} · ${expiry}`,
        mono: true,
        title:
          "The last four characters of the client identifier, which is a public value. Not the secret's: a secret is kept as an argon2id digest and no four characters can be recovered from one. Q-150.",
      },
      {
        text: "",
        absent: true,
        title:
          "API-03 owns webhook delivery and publishes nothing a browser can call — its three operations are all under /internal/v1, which the gateway does not expose. Q-195.",
      },
    ],
    state,
    // The design tints a row for its `human` and `failed` classes only, so a credential awaiting
    // approval is tinted and nothing else on this board is.
    ...(state.classKey === "human" ? { tinted: true } : {}),
    detail: [
      { label: "Tenant", value: credential.tenantId, mono: true },
      { label: "Partner", value: credential.partnerId, mono: true },
      { label: "Credential", value: credential.credentialId, mono: true },
      { label: "Status", value: credential.status },
      { label: "Expires", value: asDay(credential.expiresAt) },
      {
        label: "Revoked",
        value: credential.revokedAt === null ? "" : asDay(credential.revokedAt),
        ...(credential.revokedAt === null ? { absent: true } : {}),
      },
    ],
    /*
     * The design's `History` block, with the two entries a credential genuinely has.
     *
     * That block reads `Created`, `Last changed` and `Version` with a person's name beside the first
     * two on every page of every one of these design files. API-02 records the actor of every
     * transition in `credential_audit` and publishes none of it — Q-107 is where the missing actor
     * on every `History` block lives — so these are the instants without the names.
     */
    history: [
      { label: "Created", value: asDay(credential.createdAt) },
      { label: "Last changed", value: asDay(credential.updatedAt) },
      { label: "Version", value: `v${String(credential.version)}` },
    ],
    // The design takes the detail panel's glyph from the page's own form icon, which here is `hub`.
    glyph: "hub",
  };
}

/** The states this listing can answer, in the order `stateOf` decides them. */
export const CREDENTIAL_STATES: readonly string[] = [
  "awaiting approval",
  "revoked",
  "expired",
  "key expiring",
  "healthy",
];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(
  credentials: readonly PartnerCredentialSummary[],
  now: Date,
): readonly string[] {
  const present = new Set(credentials.map((credential) => stateOf(credential, now).label));
  return CREDENTIAL_STATES.filter((label) => present.has(label));
}

/**
 * How many tenants the rows on screen came from, in the design's own `—` grammar when there are
 * none.
 *
 * Said because the page is a board of every partner on the platform and cannot show that it is:
 * nothing publishes how many tenants exist, so "11 partners across 5 of 7 tenants" is unsayable and
 * "credentials from 3 tenants" is exactly true. `platform-audit.ts`'s `tenantSpanNote` is the same
 * sentence for the same reason, one page over.
 */
export function tenantSpanNote(
  credentials: readonly PartnerCredentialSummary[] | undefined,
): string | undefined {
  if (credentials === undefined) return undefined;
  const tenants = new Set(credentials.map((credential) => credential.tenantId)).size;
  if (tenants === 0) return undefined;
  return `Credentials from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}. The Webhook column has no source: API-03 publishes nothing a browser can call.`;
}

/**
 * Why the designed `Add a partner` form is drawn and refused.
 *
 * Its fields are `Partner name`, `Tenant`, `Scopes` and `Webhook URL`, and its primary is
 * `Create credentials`. Three reasons, and any one is enough:
 *
 *   - **The issue command is `/internal/v1` only**, which the gateway does not expose, and it takes
 *     a `partnerId` on the path that this form has no field for and nothing publishes a list of.
 *   - **It requires a `kmsKeyReference` and two distinct `approvalReferences`**, and the form has a
 *     field for neither — so one administrator pressing it could not satisfy the command even if the
 *     route were public. ADR 0044 §6 records the same finding about the tenant console's form.
 *   - **`Webhook URL` is API-03's**, and API-03 publishes nothing.
 *
 * Drawn and disabled rather than removed, because `CLAUDE.md` section 1 says the add or edit form is
 * not to be removed, and rather than wired to something that would answer 404.
 */
export const CREATE_REFUSAL =
  "API-02 publishes the credential issue command on /internal/v1 only, which the gateway does not expose, and it requires a KMS key reference and two distinct approvals — this form has a field for neither. The Webhook URL is API-03's, which publishes nothing public. Q-153, Q-195.";
